import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tickets, type TicketPriority } from '../lib/api';
import { EmailChipsInput } from '../components/EmailChipsInput';
import { TicketLinkTextarea } from '../components/TicketLinkTextarea';

export function NewTicketPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isCustomerService, setIsCustomerService] = useState(false);
  const [priority, setPriority] = useState<TicketPriority>('NORMAL');
  const [dueDate, setDueDate] = useState('');
  const [rush, setRush] = useState(false);
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await Tickets.create({
        title: title.trim(),
        description: description.trim(),
        priority,
        area: 'OTHER',
        intakeChannel: 'EMAIL',
        cause: 'NEW_PROBLEM',
        isCustomerService,
        dueDate: dueDate || null,
        rush,
        notifyEmails: notifyEmails.length > 0 ? notifyEmails : undefined,
      });
      navigate(`/tickets/${res.ticket.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="max-w-xl mx-auto space-y-4">
      <h1 className="text-lg font-semibold text-slate-900">New ticket</h1>
      <p className="text-sm text-slate-500">
        Get the report in fast — everything else (area, customer, blame, steps to reproduce, notifications) can be filled in on the ticket page after it's created.
      </p>

      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">Title *</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          autoFocus
          className="input"
        />
      </label>

      <div>
        <span className="block text-xs text-slate-500 mb-1">Description *</span>
        <TicketLinkTextarea
          value={description}
          onChange={setDescription}
          required
          rows={6}
          placeholder="Describe the issue. Type # to link a related ticket."
          className="input"
        />
      </div>

      <label className="flex items-start gap-2 cursor-pointer p-3 bg-amber-50 border border-amber-200 rounded-md">
        <input
          type="checkbox"
          checked={isCustomerService}
          onChange={(e) => setIsCustomerService(e.target.checked)}
          className="mt-0.5"
        />
        <div>
          <div className="text-sm font-medium text-amber-900">Customer service issue</div>
          <div className="text-xs text-amber-800">
            Usually affects a single customer. Emails get a <code className="font-mono">[CS]</code> prefix.
          </div>
        </div>
      </label>

      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">Priority</span>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as TicketPriority)}
          className="input"
        >
          <option value="LOW">Low</option>
          <option value="NORMAL">Normal</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </select>
      </label>

      <label className="block">
        <span className="block text-xs text-slate-500 mb-1">Order due date (optional)</span>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="input"
        />
        <p className="text-xs text-slate-500 mt-1">
          The customer order's due date. Anything due today is filed as Urgent automatically.
        </p>
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={rush}
          onChange={(e) => setRush(e.target.checked)}
          className="h-4 w-4"
        />
        <span className={rush ? 'text-sm font-semibold text-rose-600' : 'text-sm text-slate-800'}>
          Rush order
        </span>
      </label>

      <div>
        <span className="block text-xs text-slate-500 mb-1">
          Also notify these people (optional)
        </span>
        <EmailChipsInput
          value={notifyEmails}
          onChange={setNotifyEmails}
          placeholder="name@example.com, another@example.com"
        />
        <p className="text-xs text-slate-500 mt-1">
          The default distribution list is always notified. Use this to CC additional addresses on the acknowledgement, blocked, and resolution emails.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !title.trim() || !description.trim()}
          className="px-4 py-2 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60 sm:ml-auto"
        >
          {submitting ? 'Creating…' : 'Create ticket'}
        </button>
      </div>
    </form>
  );
}
